import { Box, Button, Heading, Spinner, Text, useToast, VStack } from '@chakra-ui/react'
import { AddIcon } from '@chakra-ui/icons'
import { useState } from 'react'
import { LocationCard } from './components/LocationCard'
import type { Location } from '../../types/location'
import { LocationModal } from './components/LocationModal'
import type { LocationFormValues } from './components/LocationModal'
import {
  useGetLocationsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useAssignPrescribersMutation,
} from './locationSlice'
import { useAppSelector } from '../../app/typedReduxHooks'
import { AssignPrescribersModal } from './components/AssignPrescribersModal'
import { AxiosResponse } from 'axios'

export const LocationsPage = () => {
  const toast = useToast()
  const { data: locations = [], isLoading, isError } = useGetLocationsQuery()
  const [createLocation, { isLoading: isCreating }] = useCreateLocationMutation()
  const [updateLocation, { isLoading: isUpdating }] = useUpdateLocationMutation()
  const users = useAppSelector((state) => state.users.data ?? [])

  const [editingLocation, setEditingLocation] = useState<Location | null>(null)
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false)
  const [assigningLocation, setAssigningLocation] = useState<Location | null>(null)

  const [assignPrescribers, { isLoading: isAssigning }] = useAssignPrescribersMutation()
  const { currentClinic } = useAppSelector((state) => state.clinic)

  const handleAddLocation = () => {
    setEditingLocation(null)
    setIsLocationModalOpen(true)
  }

  const handleEdit = (location: Location) => {
    setEditingLocation(location)
    setIsLocationModalOpen(true)
  }

  const handleToggleActive = async (location: Location): Promise<boolean> => {
    const result = await updateLocation({
      locationId: location.id,
      location: { status: !location.status },
    })

    if ('error' in result) {
      const error = result.error as { data?: { message?: string } }
      toast({
        title: 'Failed to update location',
        description: error.data?.message ?? 'Something went wrong. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      })
      return false
    }

    toast({
      title: 'Location updated',
      description: 'Location updated successfully',
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top',
    })

    return true
  }

  const handleOpenAssignPrescribers = (location: Location) => {
    setAssigningLocation(location)
  }

  const handleCloseAssignPrescribers = () => {
    setAssigningLocation(null)
  }

  const handleAssignPrescribers = async (selectedPrescriberIds: string[]) => {
    if (!assigningLocation) return

    const result = await assignPrescribers({
      locationId: assigningLocation.id,
      prescriberIds: selectedPrescriberIds,
    })

    if ('error' in result) {
      const error = result.error as AxiosResponse
      if (error?.status === 409) {
        toast({
          title: 'Partial success',
          description: error.data?.message || 'Some prescribers could not be assigned.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top',
        })
        setAssigningLocation(null)
      } else {
        toast({
          title: 'Failed to assign prescribers',
          description: 'Something went wrong. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        })
      }
    } else {
      toast({
        title: 'Prescribers assigned successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      })
      setAssigningLocation(null)
    }
  }

  const handleCloseLocationModal = () => {
    setIsLocationModalOpen(false)
    setEditingLocation(null)
  }

  const handleSaveLocation = async ({
    name,
    address1,
    address2,
    suburb,
    state,
    postcode,
    phoneNumber,
    email,
  }: LocationFormValues) => {
    const payload = {
      name,
      address1: address1,
      address2: address2,
      suburb: suburb,
      state: state,
      postcode: postcode,
      phoneNumber: phoneNumber,
      email: email,
    }

    const result = editingLocation
      ? await updateLocation({ locationId: editingLocation.id, location: payload })
      : await createLocation(payload)

    if ('data' in result) {
      handleCloseLocationModal()
      toast({
        title: editingLocation ? 'Location updated' : 'Location created',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      })
    } else {
      toast({
        title: editingLocation ? 'Failed to update location' : 'Failed to create location',
        description: 'Something went wrong. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  if (isLoading) {
    return (
      <Box px={6} py={2} display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Spinner size="lg" color="blue.500" />
      </Box>
    )
  }

  if (isError) {
    return (
      <Box px={6} py={2}>
        <Text color="red.500">Failed to load locations. Please try again.</Text>
      </Box>
    )
  }

  return (
    <Box>
      <Box mb={6} display="flex" justifyContent="space-between" alignItems="center">
        <VStack align="flex-start" spacing={1}>
          <Heading size="lg">Locations</Heading>
          <Text color="gray.600">Manage your clinic locations and assigned prescribers</Text>
        </VStack>
        <Button
          colorScheme="blue"
          leftIcon={<AddIcon boxSize={3} />}
          onClick={handleAddLocation}
          aria-label="Add location"
        >
          Add location
        </Button>
      </Box>

      {locations.length === 0 ? (
        <Box
          py={16}
          textAlign="center"
          border="1px"
          borderColor="gray.200"
          borderRadius="lg"
          bg="white"
        >
          <Text fontSize="lg" fontWeight="semibold" color="gray.700">
            No locations yet
          </Text>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Add your first location to get started.
          </Text>
        </Box>
      ) : (
        <VStack spacing={4} align="stretch">
          {locations.map((location) => (
            <LocationCard
              key={location.id}
              location={location}
              prescribers={location.prescribers ?? []}
              allPrescribersAutoAssigned={location.isDefault}
              onEdit={handleEdit}
              onToggleActive={handleToggleActive}
              onAssignPrescribers={handleOpenAssignPrescribers}
            />
          ))}
        </VStack>
      )}

      <LocationModal
        isOpen={isLocationModalOpen}
        mode={editingLocation ? 'edit' : 'add'}
        location={editingLocation}
        clinicPhone={currentClinic?.phone_number}
        clinicEmail={currentClinic?.email}
        isSaving={isCreating || isUpdating}
        onClose={handleCloseLocationModal}
        onSave={handleSaveLocation}
      />

      <AssignPrescribersModal
        isOpen={Boolean(assigningLocation)}
        location={assigningLocation}
        users={users}
        isAssigning={isAssigning}
        onClose={handleCloseAssignPrescribers}
        onAssign={handleAssignPrescribers}
      />
    </Box>
  )
}
